import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtUGX, fmtNum, shortDateTime, shortDate } from "@/lib/format";
import {
  Building2, Users, Store, Bus, Banknote, GraduationCap, Search, MapPin, Link2,
  Activity, ShieldCheck, Bell, TrendingUp, Heart, BookOpen, Megaphone, FileText,
  Award, AlertTriangle, Wallet, Sparkles, CalendarClock, Phone, Mail,
  Download, ChevronRight, Crown, Brain, Send,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { LiveBusMap } from "@/components/live-bus-map";
import { DashboardSkeleton } from "@/components/dashboard-skeleton";
import { SchoolOnboardingWizard } from "@/features/school-onboarding-wizard";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { SchoolAdminSidebar, type SchoolAdminModule } from "@/components/school-admin-sidebar";
import { ProcurementWorkspace } from "@/features/school-admin/procurement";
import { ImportWizard } from "@/features/school-admin/import-wizard";
import { AcademicsAdmin } from "@/features/school-admin/academics-admin";
import { InternetAdmin } from "@/features/school-admin/internet-admin";
import { AuditPanel } from "@/features/school-admin/audit-panel";
import { InvitationsPanel } from "@/features/school-admin/invitations";
import { AuditLogsPanel as TenantAuditLogsPanel } from "@/features/audit/audit-logs-panel";
import { InvitationsPanel as TenantInvitationsPanel } from "@/features/invitations/invitations-panel";
import { MerchantApprovalsPanel } from "@/features/school-admin/merchant-approvals";
import { UssdVerificationPanel } from "@/components/ussd-verification-panel";
import { StudentDetailModal } from "@/features/school-admin/student-detail-modal";
import {
  WalletOversightPanel, FeesBillingPanel, AttendanceWelfarePanel,
  BoardingPanel, HealthBayPanel, MarketplacePanel, ReportsPanel, SettingsRbacPanel,
} from "@/features/school-admin/detail-panels";
import { HealthCareModule } from "@/features/school-admin/health-care-module";
import { TenantBrandQuickSettings } from "@/features/tenant-brand-quick-settings";
import { TenantVisibilitySettings } from "@/features/tenant-visibility-settings";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

function hash(s: string) { let h = 0; for (let i=0;i<s.length;i++) h = ((h<<5) - h + s.charCodeAt(i)) | 0; return Math.abs(h); }
const fmtCompact = (n: number) => n >= 1e9 ? `UGX ${(n/1e9).toFixed(1)}B` : n >= 1e6 ? `UGX ${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `UGX ${(n/1e3).toFixed(0)}K` : `UGX ${n}`;

const MODULE_TO_TAB: Record<SchoolAdminModule, string> = {
  cockpit: "overview", students: "students", invitations: "invitations", import: "import",
  academics: "academics2", attendance: "attendance",
  fees: "fees", wallets: "wallets", procurement: "procurement",
  transport: "transport", boarding: "boarding", health: "health",
  merchants: "merchants", internet: "internet", marketplace: "marketplace",
  reports: "reports", audit: "audit", settings: "settings",
};

export function SchoolAdminDashboard({ disabledModules }: { disabledModules?: string[] } = {}) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const initialModule: SchoolAdminModule = "cockpit";
  const [module, setModule] = useState<SchoolAdminModule>(initialModule);
  const isDisabled = (m: SchoolAdminModule) => (disabledModules ?? []).includes(m);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["my-profile"],
    queryFn: async () => (await supabase.from("profiles").select("*").eq("id", user!.id).maybeSingle()).data,
  });

  const { data: myAdminSchools = [] } = useQuery({
    queryKey: ["my-admin-schools"],
    queryFn: async () => {
      const { data } = await supabase.rpc("list_my_admin_schools");
      return (data ?? []) as any[];
    },
  });

  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);
  const [studentModalId, setStudentModalId] = useState<string | null>(null);

  const schoolId = selectedSchoolId
    ?? (myAdminSchools.find((s: any) => s.is_primary)?.school_id
        ?? myAdminSchools[0]?.school_id
        ?? profile?.school_id
        ?? null);

  const { data: school } = useQuery({
    queryKey: ["my-school", schoolId],
    enabled: !!schoolId,
    queryFn: async () => (await supabase.from("schools").select("*").eq("id", schoolId!).maybeSingle()).data,
  });
  const { data: students = [] } = useQuery({
    queryKey: ["school-students", schoolId],
    enabled: !!schoolId,
    queryFn: async () => (await supabase.from("students").select("*").eq("school_id", schoolId!).order("full_name")).data ?? [],
  });
  const { data: merchants = [] } = useQuery({
    queryKey: ["school-merchants", schoolId],
    enabled: !!schoolId,
    queryFn: async () => (await supabase.from("merchants").select("*").eq("school_id", schoolId!)).data ?? [],
  });
  const { data: routes = [] } = useQuery({
    queryKey: ["school-routes", schoolId],
    enabled: !!schoolId,
    queryFn: async () => {
      const studentIds = (await supabase.from("students").select("id").eq("school_id", schoolId!)).data?.map(s => s.id) ?? [];
      if (!studentIds.length) return [];
      const { data } = await supabase.from("transport_schedule").select("*").in("student_id", studentIds);
      return data ?? [];
    },
  });
  const { data: settlements = [] } = useQuery({
    queryKey: ["school-settlements", schoolId],
    enabled: !!schoolId,
    queryFn: async () => (await supabase.from("settlements").select("*").eq("school_id", schoolId!).order("created_at", { ascending: false }).limit(50)).data ?? [],
  });
  const { data: feeTerms = [] } = useQuery({
    queryKey: ["school-fees", schoolId],
    enabled: !!schoolId && students.length > 0,
    queryFn: async () => (await supabase.from("student_fee_terms").select("*").in("student_id", students.map(s => s.id))).data ?? [],
  });
  const { data: healthVisits = [] } = useQuery({
    queryKey: ["school-health", schoolId],
    enabled: !!schoolId && students.length > 0,
    queryFn: async () => (await supabase.from("health_visits").select("*").in("student_id", students.map(s => s.id)).order("visit_date", { ascending: false }).limit(20)).data ?? [],
  });

  const filteredStudents = useMemo(() => students.filter(s =>
    !q || s.full_name.toLowerCase().includes(q.toLowerCase()) || s.student_no.includes(q)
  ), [students, q]);

  const h = schoolId ? hash(schoolId) : 0;

  // Synthesized 14-day flow series
  const flowSeries = useMemo(() => Array.from({ length: 14 }, (_, i) => ({
    d: shortDate(new Date(Date.now() - (13 - i) * 864e5).toISOString()).slice(0, 6),
    topups: 800_000 + ((h + i * 13) % 600_000),
    spend: 350_000 + ((h + i * 7) % 400_000),
    fees: i % 3 === 0 ? 1_200_000 + ((h + i * 3) % 800_000) : 200_000 + ((h + i) % 300_000),
  })), [h]);

  const classDistribution = useMemo(() => {
    const m = new Map<string, number>();
    students.forEach(s => {
      const k = (s.class_label ?? "—").split("·")[0].trim();
      m.set(k, (m.get(k) ?? 0) + 1);
    });
    return Array.from(m.entries()).map(([name, value]) => ({ name, value }));
  }, [students]);

  if (profileLoading) return <DashboardSkeleton label="Loading your profile…" />;
  if (!schoolId) return <SchoolPicker userId={user!.id} />;
  if (!school) return <DashboardSkeleton label="Warming up your school console…" />;

  const expected = feeTerms.reduce((a, f) => a + (f.total_ugx ?? 0), 0);
  const collected = feeTerms.reduce((a, f) => a + (f.paid_ugx ?? 0), 0);
  const collectionRate = expected ? collected / expected : 0;
  const netSettled = settlements.reduce((a, s) => a + (s.net_ugx ?? 0), 0);
  const grossSettled = settlements.reduce((a, s) => a + (s.gross_ugx ?? 0), 0);
  const attendance = 88 + (h % 10);
  const academicAvg = 70 + (h % 22);
  const parentEngagement = 60 + (h % 30);
  const healthScore = (school as any)?.health_score ?? 78;
  const boarders = students.filter(s => s.boarding).length;

  const radarData = [
    { axis: "Academic", v: academicAvg },
    { axis: "Attendance", v: attendance },
    { axis: "Welfare", v: 80 + (h % 15) },
    { axis: "Engagement", v: parentEngagement },
    { axis: "Collection", v: Math.round(collectionRate * 100) },
    { axis: "Compliance", v: 85 + (h % 12) },
  ];

  const CLASS_COLORS = ["#7B1416", "#A42022", "#C4A882", "#0ea5e9", "#6B5535", "#16a34a", "#ef4444"];


  const counts = {
    students: students.length,
    merchants: merchants.length,
    transport: routes.length,
  };

  const termWeek = 1 + ((Math.floor(Date.now() / 86400000) + h) % 13);
  const todayStr = new Date().toLocaleDateString("en-UG", { weekday: "long", day: "numeric", month: "long" });

  // Live ticker fragments — pulse row
  const ticker = [
    { icon: Users, label: "Attendance today", v: `${attendance}%`, tone: "emerald" },
    { icon: Bus, label: "Buses on route", v: `${Math.min(routes.length, 12)} live`, tone: "sky" },
    { icon: Banknote, label: "Fees today", v: fmtCompact(80_000 + (h % 900_000)), tone: "gold" },
    { icon: Store, label: "POS trx", v: fmtNum(60 + (h % 140)), tone: "violet" },
    { icon: Heart, label: "Sick bay", v: `${healthVisits.filter(v => v.status !== "resolved").length} open`, tone: "rose" },
    { icon: Bell, label: "Alerts", v: `${1 + (h % 4)}`, tone: "amber" },
  ] as const;

  return (
    <SidebarProvider defaultOpen>
      <SchoolAdminSidebar current={module} onSelect={(m) => setModule(isDisabled(m) ? "cockpit" : m)} schoolName={school?.name} counts={counts} disabledModules={disabledModules} />
      <SidebarInset className="bg-gradient-to-b from-[color-mix(in_oklab,var(--forest)_3%,transparent)] via-background to-background">
        {/* Sticky top bar */}
        <div className="sticky top-0 z-30 flex items-center gap-3 border-b bg-background/85 px-3 py-2 backdrop-blur md:px-5">
          <SidebarTrigger className="md:hidden" />
          <div className="hidden md:flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {todayStr} · Term 2 · Week {termWeek}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative hidden md:block">
              <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to student, merchant, route…" className="h-8 w-72 pl-8 text-xs" />
              <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border bg-muted px-1 py-0.5 font-mono text-[9px] text-muted-foreground md:inline">⌘K</kbd>
            </div>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"><Bell className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => toast.success("Broadcast composer opened")}>
              <Megaphone className="h-3.5 w-3.5" /><span className="hidden md:inline">Broadcast</span>
            </Button>
            <Button size="sm" className="h-8 gap-1 bg-[var(--forest)] text-primary-foreground hover:opacity-90" onClick={() => toast.success("Term report queued")}>
              <FileText className="h-3.5 w-3.5" /><span className="hidden md:inline">Term report</span>
            </Button>
          </div>
        </div>

        <div className="space-y-5 p-4 md:p-6">
          {/* ============ EXECUTIVE HERO ============ */}
          <Card className="relative overflow-hidden border-0 p-0 text-primary-foreground shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-[#064e3b] via-[#0a3b2c] to-[#031f16]" />
            {/* Ornamental grid */}
            <div className="absolute inset-0 opacity-[0.07]" style={{
              backgroundImage: "linear-gradient(rgba(201,168,76,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,.6) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }} />
            {/* Gold arc */}
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(201,168,76,.55), transparent 70%)" }} />

            <div className="relative grid gap-6 p-6 md:grid-cols-[1.4fr_1fr_auto] md:p-8">
              {/* Identity */}
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#e8d9a4]">
                  <span className="h-px w-6 bg-[#c9a84c]" /> School Admin Console
                </div>
                <h1 className="mt-3 font-display text-[2.75rem] leading-[1.05] tracking-tight md:text-[3.4rem]">
                  {school?.name ?? "…"}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-primary-foreground/80">
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{school?.district} · {school?.region}</span>
                  {school?.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{school.phone}</span>}
                  {school?.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{school.email}</span>}
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  <PillGold>Plan · {school?.subscription_plan ?? "growth"}</PillGold>
                  <PillGold>{(school as any)?.school_type ?? "Mixed"} · {(school as any)?.curriculum ?? "UNEB"}</PillGold>
                  <PillGold>{boarders} boarders · {students.length - boarders} day</PillGold>
                  <PillGold>{merchants.length} merchants live</PillGold>
                </div>
                {myAdminSchools.length > 1 && (
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#e8d9a4]">Managing</span>
                    <Select value={schoolId ?? undefined} onValueChange={(v) => setSelectedSchoolId(v)}>
                      <SelectTrigger className="h-8 w-64 bg-white/10 border-white/20 text-primary-foreground text-xs">
                        <SelectValue placeholder="Switch school" />
                      </SelectTrigger>
                      <SelectContent>
                        {myAdminSchools.map((s: any) => (
                          <SelectItem key={s.school_id} value={s.school_id}>
                            {s.name}{s.is_primary ? " · primary" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <PillGold>{myAdminSchools.length} schools</PillGold>
                  </div>
                )}
              </div>

              {/* Number wall */}
              <div className="grid grid-cols-2 gap-3">
                <BigStat label="Health index" v={healthScore} suffix="/100" tint="#34d399" />
                <BigStat label="Attendance" v={attendance} suffix="%" tint="#c9a84c" />
                <BigStat label="Collection" v={Math.round(collectionRate * 100)} suffix="%" tint="#60a5fa" />
                <BigStat label="Compliance" v={85 + (h % 12)} suffix="%" tint="#a78bfa" />
              </div>

              {/* Action stack */}
              <div className="flex flex-col gap-2 md:min-w-[190px]">
                <Button size="sm" className="justify-start gap-2 bg-[#c9a84c] text-[#1C1917] shadow-lg hover:bg-[#d4b660]" onClick={() => toast.success("Reconciliation started")}>
                  <ShieldCheck className="h-3.5 w-3.5" />Reconcile ledger
                </Button>
                <Button size="sm" variant="outline" className="justify-start gap-2 border-white/25 bg-white/5 text-primary-foreground hover:bg-white/15" onClick={() => setModule("procurement")}>
                  <Store className="h-3.5 w-3.5" />Procurement queue
                </Button>
                <Button size="sm" variant="outline" className="justify-start gap-2 border-white/25 bg-white/5 text-primary-foreground hover:bg-white/15" onClick={() => setModule("transport")}>
                  <Bus className="h-3.5 w-3.5" />Live fleet
                </Button>
                <Button size="sm" variant="outline" className="justify-start gap-2 border-white/25 bg-white/5 text-primary-foreground hover:bg-white/15" onClick={() => toast.success("Export queued")}>
                  <Download className="h-3.5 w-3.5" />Export data
                </Button>
              </div>
            </div>

            {/* Live ticker */}
            <div className="relative border-t border-white/10 bg-black/25 px-4 py-2.5 md:px-8">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-[11px]">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#c9a84c]">
                  <Activity className="h-3 w-3 animate-pulse" />Live pulse
                </span>
                {ticker.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-primary-foreground/85">
                    <t.icon className="h-3 w-3 text-[#c9a84c]" />
                    <span className="text-primary-foreground/60">{t.label}</span>
                    <span className="font-mono font-semibold text-primary-foreground">{t.v}</span>
                  </span>
                ))}
              </div>
            </div>
          </Card>

          {module === "cockpit" && (
            <DemoDataBanner schoolId={schoolId!} studentCount={students.length} />
          )}



      <Tabs value={MODULE_TO_TAB[module]} onValueChange={(v) => {
        const found = (Object.entries(MODULE_TO_TAB) as [SchoolAdminModule, string][]).find(([, t]) => t === v);
        if (found) setModule(found[0]);
      }}>
        <TabsList className="sr-only">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
          <TabsTrigger value="import">Import</TabsTrigger>
          <TabsTrigger value="fees">Fees</TabsTrigger>
          <TabsTrigger value="wallets">Wallets</TabsTrigger>
          <TabsTrigger value="procurement">Procurement</TabsTrigger>
          <TabsTrigger value="merchants">Merchants</TabsTrigger>
          <TabsTrigger value="transport">Transport</TabsTrigger>
          <TabsTrigger value="boarding">Boarding</TabsTrigger>
          <TabsTrigger value="health">Health &amp; Care</TabsTrigger>
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="academics2">Academics</TabsTrigger>
          <TabsTrigger value="internet">Internet</TabsTrigger>
          <TabsTrigger value="marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="ai">AI</TabsTrigger>
          <TabsTrigger value="finance">Finance</TabsTrigger>
          <TabsTrigger value="welfare">Welfare</TabsTrigger>
          <TabsTrigger value="comms">Comms</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
        </TabsList>


        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
            <Card className="p-4 shadow-card">
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Treasury cadence</div>
                  <div className="font-display text-xl">Money flow · last 14 days</div>
                </div>
                <div className="flex flex-wrap gap-2 text-[10px]">
                  <span className="inline-flex items-center gap-1"><Dot c="#0d7a5f" />Top-ups</span>
                  <span className="inline-flex items-center gap-1"><Dot c="#c9a84c" />Spend</span>
                  <span className="inline-flex items-center gap-1"><Dot c="#0ea5e9" />Fees</span>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <AreaChart data={flowSeries}>
                    <defs>
                      <linearGradient id="gtopups" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0d7a5f" stopOpacity={0.55}/><stop offset="100%" stopColor="#0d7a5f" stopOpacity={0}/></linearGradient>
                      <linearGradient id="gfees" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.35}/><stop offset="100%" stopColor="#0ea5e9" stopOpacity={0}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="d" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1e6).toFixed(1)}M`} />
                    <Tooltip formatter={(v: any) => fmtUGX(v)} />
                    <Area type="monotone" dataKey="topups" stroke="#0d7a5f" strokeWidth={2} fill="url(#gtopups)" />
                    <Area type="monotone" dataKey="fees" stroke="#0ea5e9" strokeWidth={2} fill="url(#gfees)" />
                    <Line type="monotone" dataKey="spend" stroke="#c9a84c" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 border-t pt-3 text-center">
                <StatMini label="Avg daily top-ups" v={fmtCompact(flowSeries.reduce((a, f) => a + f.topups, 0) / 14)} />
                <StatMini label="Avg daily spend" v={fmtCompact(flowSeries.reduce((a, f) => a + f.spend, 0) / 14)} />
                <StatMini label="Avg daily fees" v={fmtCompact(flowSeries.reduce((a, f) => a + f.fees, 0) / 14)} />
              </div>
            </Card>

            <Card className="p-4 shadow-card">
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Governance</div>
                  <div className="font-display text-xl">Performance radar</div>
                </div>
                <Badge variant="outline" className="text-[10px]">6-axis</Badge>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#c9a84c" strokeOpacity={0.2} />
                    <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0,100]} />
                    <Radar dataKey="v" stroke="#0d7a5f" fill="#0d7a5f" fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="p-4 shadow-card">
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Enrolment</div>
                <div className="font-display text-lg">Class distribution</div>
              </div>
              <div className="h-52">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={classDistribution} dataKey="value" nameKey="name" innerRadius={45} outerRadius={78} paddingAngle={2}>
                      {classDistribution.map((_, i) => <Cell key={i} fill={CLASS_COLORS[i % CLASS_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5 text-[10px]">
                {classDistribution.slice(0, 8).map((c, i) => (
                  <span key={c.name} className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: CLASS_COLORS[i % CLASS_COLORS.length] }} />{c.name} · {c.value}</span>
                ))}
              </div>
            </Card>

            <Card className="p-4 lg:col-span-2 shadow-card">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Situation room</div>
                  <div className="font-display text-lg">Attention queue · today</div>
                </div>
                <Badge variant="outline" className="text-[10px]"><Activity className="mr-1 h-3 w-3" />real-time</Badge>
              </div>
              <div className="divide-y text-sm">
                {[
                  { i: Wallet, tone: "emerald", t: `${fmtNum(120 + (h%80))} wallet top-ups`, w: `${fmtCompact(grossSettled || 4_200_000)} flowed in today`, cta: "Ledger" },
                  { i: Bus, tone: "sky", t: `${routes.length} scheduled journeys`, w: `${88 + (h%10)}% on-time · 2 buses idling`, cta: "Fleet" },
                  { i: BookOpen, tone: "amber", t: "Attendance synced", w: `${attendance}% present · P.4 East below 80%`, cta: "Roll" },
                  { i: Store, tone: "violet", t: `${fmtNum(80 + (h%60))} POS transactions`, w: `Top: ${merchants[0]?.name ?? "Canteen"} · ${fmtCompact(220_000 + (h%400_000))}`, cta: "POS" },
                  { i: Heart, tone: "rose", t: `${healthVisits.length} sick-bay visits this week`, w: "All resolved · 2 medications refilled", cta: "Ward" },
                  { i: Bell, tone: "gold", t: `${1 + (h%4)} alerts open`, w: "1 hardware fault · Bus 07 · Naguru corridor", cta: "Investigate" },
                ].map((r, i) => (
                  <div key={i} className="group grid grid-cols-[28px_1fr_auto] items-center gap-3 py-2.5">
                    <div className="grid h-7 w-7 place-items-center rounded-md" style={{ background: `color-mix(in oklab, ${toneColor(r.tone)} 15%, transparent)`, color: toneColor(r.tone) }}>
                      <r.i className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate">{r.t}</div>
                      <div className="truncate text-[11px] text-muted-foreground">{r.w}</div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 opacity-0 transition group-hover:opacity-100">
                      {r.cta}<ChevronRight className="ml-0.5 h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </TabsContent>



        {/* STUDENTS */}
        <TabsContent value="students" className="mt-4">
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
              <div className="font-display text-lg">Roll book · {students.length} students</div>
              <div className="flex gap-2">
                <div className="relative w-72">
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or no." className="pl-8" />
                </div>
                <Button size="sm" variant="outline"><Download className="mr-1 h-3 w-3" />CSV</Button>
              </div>
            </div>
            <div className="grid max-h-[560px] gap-px overflow-auto bg-border md:grid-cols-2">
              {filteredStudents.map(s => (
                <button key={s.id} type="button" onClick={() => setStudentModalId(s.id)}
                  className="flex items-center justify-between bg-card p-3 text-sm text-left transition hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-full text-xs font-semibold text-white" style={{ background: s.color_hex }}>{s.initials}</div>
                    <div>
                      <div className="font-medium">{s.full_name}</div>
                      <div className="text-xs text-muted-foreground">{s.student_no} · {s.class_label}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm">{fmtUGX(s.balance_ugx)}</div>
                    <div className="mt-0.5 flex gap-1 justify-end">
                      <Badge variant="outline" className="text-[9px]">{s.boarding ? "Board" : "Day"}</Badge>
                      {s.bus_route && <Badge variant="outline" className="text-[9px]">{s.bus_route}</Badge>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* FINANCE */}
        <TabsContent value="finance" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <MetricCard icon={Banknote} label="Expected (term)" value={fmtCompact(expected)} />
            <MetricCard icon={TrendingUp} label="Collected" value={fmtCompact(collected)} />
            <MetricCard icon={AlertTriangle} label="Outstanding" value={fmtCompact(Math.max(0, expected - collected))} tone="warn" />
            <MetricCard icon={Wallet} label="Net settled (30d)" value={fmtCompact(netSettled)} tone="ok" />
          </div>
          <Card className="p-4">
            <div className="mb-2 font-display text-base">Settlement batches · last 50</div>
            <div className="max-h-[420px] divide-y overflow-auto">
              {settlements.map((s: any) => (
                <div key={s.id} className="grid grid-cols-[1.5fr_1fr_1fr_1fr_auto] items-center gap-2 py-2 text-sm">
                  <div>
                    <div className="font-mono text-xs">{s.reference ?? s.id.slice(0,8)}</div>
                    <div className="text-[10px] text-muted-foreground">{shortDateTime(s.created_at)}</div>
                  </div>
                  <div className="font-mono text-xs">{fmtUGX(s.gross_ugx)} <span className="text-muted-foreground">gross</span></div>
                  <div className="font-mono text-xs text-muted-foreground">−{fmtUGX(s.fee_ugx)} fees</div>
                  <div className="font-mono font-medium">{fmtUGX(s.net_ugx)}</div>
                  <Badge variant="outline" className="text-[10px] capitalize">{s.status}</Badge>
                </div>
              ))}
              {!settlements.length && <div className="p-6 text-center text-sm text-muted-foreground">No settlements yet.</div>}
            </div>
          </Card>
          <UssdVerificationPanel />
        </TabsContent>

        {/* MERCHANTS */}
        <TabsContent value="merchants" className="mt-4 space-y-4">
          <MerchantApprovalsPanel schoolId={schoolId!} />
          <Card>
            <div className="border-b p-4 font-display text-lg">Approved merchants · {merchants.length}</div>
            <div className="grid gap-px bg-border md:grid-cols-2">
              {merchants.map((m: any) => (
                <div key={m.id} className="bg-card p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{m.category} · fee {(m.fee_rate_bps / 100).toFixed(2)}%</div>
                    </div>
                    <Badge variant={m.active ? "secondary" : "destructive"}>{m.active ? "Active" : "Suspended"}</Badge>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <Mini label="Tx (30d)" v={`${fmtNum(80 + (hash(m.id) % 200))}`} />
                    <Mini label="Volume" v={fmtCompact(400_000 + (hash(m.id) % 3_000_000))} />
                    <Mini label="Rating" v={`${(4 + (hash(m.id) % 9) / 10).toFixed(1)}★`} />
                  </div>
                </div>
              ))}
              {!merchants.length && <div className="bg-card p-6 text-center text-sm text-muted-foreground md:col-span-2">No merchants yet.</div>}
            </div>
          </Card>
        </TabsContent>

        {/* TRANSPORT */}
        <TabsContent value="transport" className="mt-4 space-y-4">
          {(() => {
            const schoolRoutes = Array.from(new Set((routes as any[]).map(r => r.route).filter(Boolean)));
            return <LiveBusMap height={520} autoTick wialon canViewTrails canViewHistory title={`Fleet telematics · ${school?.name ?? "school"}`} routes={schoolRoutes.length ? schoolRoutes : ["__none__"]} />;
          })()}
          <div className="grid gap-3 md:grid-cols-4">
            <MetricCard icon={Bus} label="Buses" value={`${3 + (h % 8)}`} />
            <MetricCard icon={MapPin} label="Routes" value={`${5 + (h % 10)}`} />
            <MetricCard icon={Users} label="Riders" value={fmtNum(Math.round(students.length * 0.32))} />
            <MetricCard icon={CalendarClock} label="On-time" value={`${88 + (h % 11)}%`} tone="ok" />
          </div>
          <Card>
            <div className="border-b p-4 font-display text-lg">Transport schedules · {routes.length}</div>
            <div className="grid max-h-[420px] gap-px overflow-auto bg-border md:grid-cols-2">
              {routes.map((r: any) => (
                <div key={r.id} className="flex items-center justify-between bg-card p-3 text-sm">
                  <div>
                    <div className="font-medium">{r.route} · {r.direction.toUpperCase()}</div>
                    <div className="text-xs text-muted-foreground">{r.pickup_time} · {r.stop}</div>
                  </div>
                  <Badge variant="outline">Day {r.day_of_week}</Badge>
                </div>
              ))}
              {!routes.length && <div className="bg-card p-6 text-center text-sm text-muted-foreground md:col-span-2">No schedules yet.</div>}
            </div>
          </Card>
        </TabsContent>

        {/* ACADEMICS */}
        <TabsContent value="academics" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <MetricCard icon={Award} label="Academic avg" value={`${academicAvg}%`} />
            <MetricCard icon={CalendarClock} label="Attendance" value={`${attendance}%`} />
            <MetricCard icon={Crown} label="Pass rate" value={`${75 + (h % 22)}%`} />
          </div>
          <Card className="p-4">
            <div className="mb-2 font-display text-base">Subject performance</div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={["Math","English","Science","Social","ICT","Arts","PE"].map((sub, i) => ({ sub, score: 55 + (h + i * 11) % 40 }))}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="sub" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
                  <Tooltip /><Bar dataKey="score" fill="#C4A882" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>

        {/* WELFARE */}
        <TabsContent value="welfare" className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <MetricCard icon={Heart} label="Sick-bay (7d)" value={`${healthVisits.length}`} />
            <MetricCard icon={ShieldCheck} label="Safe journeys" value={`${99 - (h % 4)}%`} tone="ok" />
            <MetricCard icon={Award} label="Nutrition compliance" value={`${85 - (h % 12)}%`} />
            <MetricCard icon={AlertTriangle} label="Open welfare cases" value={`${h % 4}`} tone="warn" />
          </div>
          <Card className="p-4">
            <div className="mb-2 font-display text-base">Recent health visits</div>
            <div className="divide-y text-sm">
              {healthVisits.map((v: any) => (
                <div key={v.id} className="grid grid-cols-[1.5fr_1fr_2fr_auto] items-center gap-2 py-2">
                  <div className="font-medium">{students.find(s => s.id === v.student_id)?.full_name ?? "—"}</div>
                  <div className="text-xs">{shortDate(v.visit_date)}</div>
                  <div className="text-xs text-muted-foreground">{v.reason} · {v.nurse_note}</div>
                  <Badge variant="outline" className="text-[10px] capitalize">{v.status}</Badge>
                </div>
              ))}
              {!healthVisits.length && <div className="p-6 text-center text-sm text-muted-foreground">No recent visits.</div>}
            </div>
          </Card>
        </TabsContent>

        {/* COMMS */}
        <TabsContent value="comms" className="mt-4 space-y-4">
          <Card className="p-4">
            <div className="mb-3 font-display text-base flex items-center gap-2"><Megaphone className="h-4 w-4" />Compose broadcast</div>
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Input placeholder="e.g. School closes early Friday for inter-house sports." />
              <Select defaultValue="all">
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All parents (SMS + Push)</SelectItem>
                  <SelectItem value="boarding">Boarding only</SelectItem>
                  <SelectItem value="day">Day only</SelectItem>
                  <SelectItem value="bus">Bus riders</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-[11px] text-muted-foreground">Est. recipients: {fmtNum(Math.round(students.length * 0.85))} · cost UGX {fmtNum(Math.round(students.length * 0.85) * 35)}</div>
              <Button size="sm" onClick={() => toast.success("Broadcast queued")}><Send className="mr-1 h-3 w-3" />Send</Button>
            </div>
          </Card>
          <Card className="p-4">
            <div className="mb-2 font-display text-base">Recent broadcasts</div>
            <div className="divide-y text-sm">
              {[
                { t: "Mid-term reports ready · log in to view", w: "2,134 sent · 1,820 read", d: "Today 09:12" },
                { t: "Sports day · bring bottled water", w: "1,985 sent · 1,560 read", d: "Yesterday" },
                { t: "Fees deadline in 7 days", w: "2,134 sent · 1,902 read", d: "3 days ago" },
              ].map((b, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] gap-2 py-2">
                  <div><div>{b.t}</div><div className="text-[11px] text-muted-foreground">{b.w}</div></div>
                  <div className="text-[11px] text-muted-foreground">{b.d}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* COMPLIANCE */}
        <TabsContent value="compliance" className="mt-4 space-y-4">
          <div className="grid gap-2 md:grid-cols-2">
            {[
              { k: "MoES registration", v: "Valid", ok: true },
              { k: "URA TIN", v: (school as any)?.ura_tin ?? "Pending", ok: !!(school as any)?.ura_tin },
              { k: "PCI scope", v: "In scope · compliant", ok: true },
              { k: "Data protection (DPA)", v: "Compliant · KLA-1", ok: true },
              { k: "Safeguarding policy", v: "Filed · v2.3", ok: true },
              { k: "Annual financial audit", v: "Due in 42 days", ok: false },
              { k: "Fire safety inspection", v: "Valid · Mar 2026", ok: true },
              { k: "Food handler certs", v: `${merchants.length} merchants verified`, ok: true },
            ].map(c => (
              <div key={c.k} className="rounded-lg border bg-card p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.k}</div>
                <div className={`mt-1 text-sm font-medium ${c.ok ? "text-success" : "text-amber-600"}`}>{c.v}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* AI */}
        <TabsContent value="ai" className="mt-4">
          <Card className="overflow-hidden">
            <div className="border-b bg-gradient-to-r from-primary/10 to-transparent p-4">
              <div className="flex items-center gap-2 font-display text-lg"><Brain className="h-4 w-4 text-primary" />AI Headmaster Copilot</div>
              <div className="mt-1 text-xs text-muted-foreground">Recommendations tailored to {school?.name}.</div>
            </div>
            <div className="divide-y">
              {[
                `Collection at ${Math.round(collectionRate * 100)}% — auto-send T-7 reminders to ${fmtNum(Math.max(0, Math.round(students.length * (1 - collectionRate))))} parents could lift this by 12pts.`,
                `${boarders} boarders detected. Boarding welfare deposits average ${fmtCompact(15_000 + (h%30_000))} below benchmark — surface top-up nudge.`,
                `Bus on-time at ${88 + (h%10)}% — Bus 14 underperforms by 6 minutes; reroute via Naalya bypass.`,
                `${merchants.length} merchants live. ${1 + (h%3)} canteen items are sub-1.5★ rated — flag for review.`,
                "Attendance dips on Fridays · schedule personalised SMS nudges to top-decile-absence parents.",
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-4">
                  <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Sparkles className="h-3.5 w-3.5" /></div>
                  <div className="flex-1 text-sm">{s}</div>
                  <Button size="sm" variant="outline">Apply</Button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="procurement" className="mt-4"><ProcurementWorkspace schoolId={schoolId!} /></TabsContent>
        <TabsContent value="import" className="mt-4"><ImportWizard schoolId={schoolId!} /></TabsContent>
        <TabsContent value="academics2" className="mt-4"><AcademicsAdmin schoolId={schoolId!} /></TabsContent>
        <TabsContent value="internet" className="mt-4"><InternetAdmin schoolId={schoolId!} /></TabsContent>
        <TabsContent value="audit" className="mt-4 space-y-4">
          <TenantAuditLogsPanel />
          <AuditPanel schoolId={schoolId!} />
        </TabsContent>
        <TabsContent value="invitations" className="mt-4 space-y-4">
          <TenantInvitationsPanel schoolId={schoolId!} />
          <InvitationsPanel schoolId={schoolId!} userId={user!.id} />
        </TabsContent>
        <TabsContent value="wallets" className="mt-4"><WalletOversightPanel schoolId={schoolId!} /></TabsContent>
        <TabsContent value="fees" className="mt-4"><FeesBillingPanel schoolId={schoolId!} /></TabsContent>
        <TabsContent value="attendance" className="mt-4"><AttendanceWelfarePanel schoolId={schoolId!} /></TabsContent>
        <TabsContent value="boarding" className="mt-4"><BoardingPanel schoolId={schoolId!} /></TabsContent>
        <TabsContent value="health" className="mt-4"><HealthCareModule schoolId={schoolId!} /></TabsContent>
        <TabsContent value="marketplace" className="mt-4"><MarketplacePanel schoolId={schoolId!} /></TabsContent>
        <TabsContent value="reports" className="mt-4"><ReportsPanel schoolId={schoolId!} /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsRbacPanel schoolId={schoolId!} /></TabsContent>
        <TabsContent value="branding" className="mt-4 space-y-4">
          <TenantBrandQuickSettings />
          <TenantVisibilitySettings />
        </TabsContent>


      </Tabs>
        </div>
      </SidebarInset>
      <StudentDetailModal studentId={studentModalId} open={!!studentModalId} onOpenChange={(o) => !o && setStudentModalId(null)} />
    </SidebarProvider>
  );
}

/* ============================ Sub-components ============================ */

function PillGold({ children }: { children: any }) {
  return <span className="inline-flex items-center gap-1 rounded-full border border-[#c9a84c]/40 bg-[#c9a84c]/10 px-2.5 py-0.5 text-[10px] font-medium text-[#f0dfa5]">{children}</span>;
}

function BigStat({ label, v, suffix, tint }: { label: string; v: number; suffix?: string; tint: string }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.04] p-3 backdrop-blur-sm">
      <div className="text-[9px] uppercase tracking-[0.2em] text-primary-foreground/60">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span className="font-display text-3xl leading-none" style={{ color: tint }}>{v}</span>
        <span className="text-xs text-primary-foreground/70">{suffix}</span>
      </div>
      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, v)}%`, background: tint }} />
      </div>
    </div>
  );
}

function BentoKpi({ icon: Icon, label, value, sub, trend, tint, className = "" }: { icon: any; label: string; value: string; sub?: string; trend?: number[]; tint: string; className?: string }) {
  const pts = trend && trend.length ? trend : [];
  const max = Math.max(1, ...pts);
  const min = Math.min(...pts, 0);
  const range = Math.max(1, max - min);
  const path = pts.map((p, i) => {
    const x = (i / Math.max(1, pts.length - 1)) * 100;
    const y = 24 - ((p - min) / range) * 22;
    return `${i === 0 ? "M" : "L"}${x},${y}`;
  }).join(" ");
  const area = path + ` L100,24 L0,24 Z`;
  return (
    <Card className={`relative overflow-hidden p-4 shadow-card ${className}`}>
      <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: tint }} />
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
        <div className="grid h-6 w-6 place-items-center rounded-md" style={{ background: `color-mix(in oklab, ${tint} 14%, transparent)`, color: tint }}>
          <Icon className="h-3 w-3" />
        </div>
      </div>
      <div className="mt-2 font-display text-2xl leading-tight">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
      {pts.length > 1 && (
        <svg viewBox="0 0 100 26" preserveAspectRatio="none" className="mt-2 h-8 w-full">
          <defs>
            <linearGradient id={`grad-${label.replace(/\s/g, "")}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={tint} stopOpacity="0.35" />
              <stop offset="100%" stopColor={tint} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#grad-${label.replace(/\s/g, "")})`} />
          <path d={path} fill="none" stroke={tint} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </Card>
  );
}

function StatMini({ label, v }: { label: string; v: string }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono text-xs font-medium">{v}</div>
    </div>
  );
}

function toneColor(t: string) {
  switch (t) {
    case "emerald": return "#0d7a5f";
    case "sky": return "#0ea5e9";
    case "gold": return "#c9a84c";
    case "violet": return "#a78bfa";
    case "rose": return "#f43f5e";
    case "amber": return "#f59e0b";
    default: return "#64748b";
  }
}

function Ring({ label, v, color }: { label: string; v: number; color: string }) {
  const pct = Math.min(100, Math.max(0, v));
  return (
    <div className="flex flex-col items-center">
      <div className="relative grid h-16 w-16 place-items-center rounded-full" style={{ background: `conic-gradient(${color} ${pct * 3.6}deg, rgba(255,255,255,0.15) 0deg)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-full bg-[#0a221a] font-display text-base" style={{ color }}>{v}</div>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-primary-foreground/70">{label}</div>
    </div>
  );
}


function KpiCard({ icon: Icon, label, value, sub, tone }: { icon: any; label: string; value: string; sub?: string; tone?: "forest" | "mustard" }) {
  const ring = tone === "forest" ? "ring-1 ring-emerald-200" : tone === "mustard" ? "ring-1 ring-amber-200" : "";
  return (
    <Card className={`p-4 shadow-card ${ring}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="mt-1.5 font-display text-2xl">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function MetricCard({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone?: "ok" | "warn" }) {
  const t = tone === "ok" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : "";
  return (
    <Card className="p-3 shadow-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className={`mt-1 font-display text-xl ${t}`}>{value}</div>
    </Card>
  );
}

function Mini({ label, v }: { label: string; v: string }) {
  return (
    <div className="rounded border bg-muted/30 p-1.5">
      <div className="text-[9px] uppercase text-muted-foreground">{label}</div>
      <div className="font-mono text-xs">{v}</div>
    </div>
  );
}

function Dot({ c }: { c: string }) {
  return <span className="ml-2 inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: c }} /></span>;
}

function DemoDataBanner({ schoolId, studentCount }: { schoolId: string; studentCount: number }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<"idle" | "seeding">("idle");
  const [reseedOpen, setReseedOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const seed = useMutation({
    mutationFn: async (force: boolean) => {
      setPhase("seeding");
      const { data, error } = await (supabase.rpc as any)("reseed_demo_for_school", { _school_id: schoolId, _force: force });
      if (error) throw error;
      return data as { seeded: boolean; reason?: string; students?: number };
    },
    onSuccess: (res) => {
      if (res?.seeded) toast.success("Demo data ready — reloading");
      else toast.info(res?.reason === "existing_data" ? "School already has data — use Reseed to replace" : "Nothing to seed");
      qc.invalidateQueries();
      setPhase("idle");
      setReseedOpen(false);
      setConfirmed(false);
    },
    onError: (e: Error) => { toast.error(e.message); setPhase("idle"); },
  });

  const empty = studentCount <= 3;
  const phases = ["Students & guardians", "Fee terms & billing", "Transport schedule", "Health records", "Merchants & settlements"];

  return (
    <Card className={`relative overflow-hidden border-dashed p-5 ${empty ? "bg-gradient-to-br from-amber-50 via-card to-emerald-50" : "bg-gradient-to-br from-muted/40 via-card to-card"}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="max-w-2xl">
          <Badge variant="secondary" className="gap-1"><Sparkles className="h-3 w-3" />{empty ? "Console looks empty" : "Demo data controls"}</Badge>
          <h3 className="mt-2 font-display text-xl">{empty ? "Bring your admin console to life with demo data" : "Refresh or replace your demo data"}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {empty
              ? "Seed 24 sample learners, transport routes, fee terms, health visits and 14 days of settlements. Idempotent — safe to click twice."
              : `You have ${studentCount} students. Seeding is skipped unless you confirm a re-seed, which clears demo data first.`}
          </p>
          {phase === "seeding" && (
            <div className="mt-3 space-y-1.5">
              {phases.map((p, i) => (
                <div key={p} className="flex items-center gap-2 text-xs">
                  <span className={`h-2 w-2 rounded-full ${i < 2 ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/30"}`} />
                  <span className="text-muted-foreground">{p}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {empty ? (
            <Button size="lg" disabled={seed.isPending} onClick={() => seed.mutate(false)} className="gap-2">
              <Sparkles className="h-4 w-4" />{seed.isPending ? "Seeding…" : "Seed demo data"}
            </Button>
          ) : (
            <Dialog open={reseedOpen} onOpenChange={setReseedOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2"><Sparkles className="h-4 w-4" />Re-seed demo</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Re-seed demo data?</DialogTitle>
                  <DialogDescription>
                    This will delete existing demo students, transport schedules, fee terms, health visits and settlements for this school, then repopulate fresh sample data. Real production data attached to other tenants is unaffected.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3">
                  <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} id="confirm-reseed" />
                  <label htmlFor="confirm-reseed" className="text-sm">I understand the current {studentCount} demo student records for this school will be permanently removed.</label>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setReseedOpen(false)}>Cancel</Button>
                  <Button disabled={!confirmed || seed.isPending} onClick={() => seed.mutate(true)}>
                    {seed.isPending ? "Re-seeding…" : "Confirm re-seed"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </Card>
  );
}

function SchoolPicker({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"pick" | "create">("pick");
  const [pick, setPick] = useState<string>("");
  const [q, setQ] = useState("");
  const { data: schools = [] } = useQuery({
    queryKey: ["all-schools-pick"],
    queryFn: async () => (await (supabase.from as any)("schools_public").select("id,name,district,region,subscription_plan,active").order("name")).data ?? [],
  });
  const filtered = useMemo(() => schools.filter((s: any) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.district ?? "").toLowerCase().includes(q.toLowerCase())), [schools, q]);
  const link = useMutation({
    mutationFn: async (school_id: string) => {
      const { error } = await supabase.from("profiles").update({ school_id }).eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Linked to school"); qc.invalidateQueries({ queryKey: ["my-profile"] }); },
    onError: (e: any) => toast.error(e.message ?? "Failed to link"),
  });

  if (mode === "create") {
    return (
      <SchoolOnboardingWizard
        userId={userId}
        onLinked={() => qc.invalidateQueries({ queryKey: ["my-profile"] })}
        onCancel={() => setMode("pick")}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-primary/10 via-card to-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Badge variant="secondary"><Link2 className="mr-1 h-3 w-3" />School linkage required</Badge>
            <h1 className="mt-2 font-display text-3xl">Connect your admin account to a school</h1>
            <p className="mt-1 text-sm text-muted-foreground">Pick from Uganda's TriSphere network — or onboard a brand new school in 3 minutes.</p>
          </div>
          <Button size="lg" onClick={() => setMode("create")} className="gap-2">
            <Sparkles className="h-4 w-4" />Onboard a new school
          </Button>
        </div>
      </Card>
      <Card className="p-4 space-y-3">
        <div className="grid gap-2 md:grid-cols-[1fr_320px_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search school name or district…" className="pl-9" />
          </div>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger><SelectValue placeholder="Select a school" /></SelectTrigger>
            <SelectContent>
              {filtered.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.district}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button disabled={!pick || link.isPending} onClick={() => link.mutate(pick)}>
            <Link2 className="mr-1 h-3 w-3" />Link
          </Button>
        </div>
        <div className="max-h-[420px] divide-y overflow-auto rounded-lg border">
          {filtered.map((s: any) => (
            <button key={s.id} onClick={() => setPick(s.id)} className={`grid w-full grid-cols-[1fr_auto_auto] items-center gap-2 p-3 text-left text-sm hover:bg-accent ${pick === s.id ? "bg-accent" : ""}`}>
              <div>
                <div className="font-medium">{s.name}</div>
                <div className="text-[11px] text-muted-foreground">{s.district} · {s.region}</div>
              </div>
              <Badge variant="outline" className="capitalize text-[10px]">{s.subscription_plan}</Badge>
              <Badge variant={s.active ? "secondary" : "destructive"} className="text-[10px]">{s.active ? "active" : "off"}</Badge>
            </button>
          ))}
          {!filtered.length && <div className="p-6 text-center text-sm text-muted-foreground">No schools found.</div>}
        </div>
      </Card>
    </div>
  );
}
